import React, { useEffect, useRef, useState, VFC } from 'react';
import { Organization } from 'src/common/interfaces/organization.interface';
import { Nullable } from 'src/types/nullable.type';
import { functionNoop } from 'src/utils/function/noop';
import { apiGetInvoices, apiGetSubscriptionPlans, apiGetSubscription } from 'src/utils/journeyApi';
import { Invoice } from './invoice-list';
import { DashboardSettingsBillingSection } from './section';

interface DashboardSettingsBillingProps {
  currentOrganization: Organization;
}

export type SubscriptionPaymentIntervalTypes = 'month' | 'year' | 'quarter';

export type SubscriptionTypes = 'starter' | 'pro' | 'enterprise';

export interface Subscription {
  quantity: number;
  current_period_end: number;
  cancel_at: Nullable<number>;
  cancel_at_period_end: boolean;
  pause_collection: object;
  status: Nullable<'active' | 'trialing'>;
  plan_type: Nullable<SubscriptionTypes>;
  plan: Partial<{ interval: SubscriptionPaymentIntervalTypes; interval_count?: number; transform_usage?: any }>;
  trial_start_at?: string;
  trial_end_at?: string;
  trial_expired?: boolean;
}

export interface SubscriptionPlan {
  amount: number;
  label: string;
  currency: string;
  payment_interval: SubscriptionPaymentIntervalTypes;
  quota: number;
}

export interface SubscriptionPlanObject {
  starter: SubscriptionPlan[],
  pro: SubscriptionPlan[],
  enterprise: SubscriptionPlan[]
}

export const DashboardSettingsBilling: VFC<DashboardSettingsBillingProps> = ({ currentOrganization }) => {
  const [loaded, setLoaded] = useState<Nullable<boolean>>(null);
  const invoicesRef = useRef<Invoice[]>([]);
  const subscriptionRef = useRef<Nullable<Subscription>>(null);
  const plansRef = useRef<Nullable<SubscriptionPlanObject>>(null);

  useEffect(() => {
    const fetchPlans = () => apiGetSubscriptionPlans(currentOrganization.id);
    const fetchInvoices = () => apiGetInvoices(currentOrganization.id);
    const fetchSubscription = () => apiGetSubscription(currentOrganization.id);

    if (loaded === null) {
      fetchPlans()
        .then(({ plans }) => {
          plansRef.current = plans;
          Promise.all([fetchInvoices(), fetchSubscription()])
            .then(([invoices, subscription]) => {
              // pagination is missing for now, need data
              invoicesRef.current = invoices.data;
              subscriptionRef.current = subscription;
              plansRef.current = plans;
            })
            .catch(functionNoop)
            .then(() => {
              // subscription and invoices return 404 if there is none
              setLoaded(true);
            });
        })
        .catch((err) => {
          console.error(err);
          setLoaded(false);
        });
    }
  }, [currentOrganization.id, loaded]);

  return (
    <div className='mt-6'>
      {loaded === false ? (
        <span>An error occurred. Please get in touch with support</span>
      ) : (
        <DashboardSettingsBillingSection
          loading={loaded === null}
          invoices={invoicesRef.current}
          plans={plansRef.current}
          subscription={subscriptionRef.current}
        />
      )}
    </div>
  );
};
